import React, { useEffect, useState } from "react";
import { api, formatApiError , toastError} from "../../lib/api";
import { PageHeader, EmptyState } from "../../components/domain/Bits";
import { Input } from "../../components/ui/input";
import { FileClock } from "lucide-react";
import { toast } from "sonner";

const ACTION_COLORS = {
  CREATE: "text-emerald-700 bg-emerald-50 border-emerald-200",
  UPDATE: "text-blue-700 bg-blue-50 border-blue-200",
  DELETE: "text-rose-700 bg-rose-50 border-rose-200",
  ARCHIVE: "text-slate-700 bg-slate-50 border-slate-200",
  RESTORE: "text-emerald-700 bg-emerald-50 border-emerald-200",
  LOGIN: "text-indigo-700 bg-indigo-50 border-indigo-200",
  LOGOUT: "text-indigo-700 bg-indigo-50 border-indigo-200",
  STAGE_MOVE: "text-violet-700 bg-violet-50 border-violet-200",
  STATUS_CHANGE: "text-amber-700 bg-amber-50 border-amber-200",
  SUSPEND: "text-amber-700 bg-amber-50 border-amber-200",
  REACTIVATE: "text-emerald-700 bg-emerald-50 border-emerald-200",
  APPLY: "text-fuchsia-700 bg-fuchsia-50 border-fuchsia-200",
  APPLY_REJECTED: "text-rose-700 bg-rose-50 border-rose-200",
  PASSWORD_CHANGE: "text-slate-700 bg-slate-50 border-slate-200",
  PASSWORD_RESET: "text-slate-700 bg-slate-50 border-slate-200",
  MFA_ENABLE: "text-emerald-700 bg-emerald-50 border-emerald-200",
};

export default function AuditLog() {
  const [rows, setRows] = useState([]);
  const [entityType, setEntityType] = useState("");
  const [action, setAction] = useState("");
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    try {
      const params: Record<string, string> = {};
      if (entityType) params.entity_type = entityType;
      if (action) params.action = action;
      const { data } = await api.get("/audit", { params });
      setRows(data);
    } catch (e) { toastError(e); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [entityType, action]);

  return (
    <>
      <PageHeader title="Audit Log" subtitle="Append-only ledger of all mutations" />
      <div className="max-w-7xl mx-auto p-6 md:p-8 space-y-4">
        <div className="flex gap-2">
          <Input placeholder="Filter by entity_type" value={entityType} onChange={(e) => setEntityType(e.target.value)} className="max-w-xs" data-testid="audit-entity-filter" />
          <Input placeholder="Filter by action" value={action} onChange={(e) => setAction(e.target.value)} className="max-w-xs" data-testid="audit-action-filter" />
        </div>
        {rows.length === 0 ? (
          <EmptyState icon={FileClock} title="No audit events" />
        ) : (
          <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
            <table className="w-full text-sm font-mono" data-testid="audit-table">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-widest">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium">Time</th>
                  <th className="text-left px-4 py-2.5 font-medium">Actor</th>
                  <th className="text-left px-4 py-2.5 font-medium">Entity</th>
                  <th className="text-left px-4 py-2.5 font-medium">Action</th>
                  <th className="text-left px-4 py-2.5 font-medium">Entity ID</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <React.Fragment key={r.id}>
                    <tr className="border-t border-slate-200 hover:bg-slate-50/60 cursor-pointer"
                        onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                        data-testid={`audit-row-${r.id.slice(0,8)}`}>
                      <td className="px-4 py-2 text-xs text-slate-500">{new Date(r.occurred_at).toLocaleString()}</td>
                      <td className="px-4 py-2 text-xs" data-testid={`audit-actor-${r.id.slice(0,8)}`}>
                        {r.actor_name || r.actor_email ? (
                          <div className="font-sans">
                            <div className="font-medium text-slate-800">{r.actor_name || r.actor_email}</div>
                            {r.actor_email && r.actor_name && (
                              <div className="text-[11px] text-slate-500">{r.actor_email}</div>
                            )}
                            {r.actor_role && (
                              <div className="text-[11px] text-slate-500">
                                {String(r.actor_role).replace(/_/g, " ")} <span className="text-slate-400">({r.actor_scope})</span>
                              </div>
                            )}
                          </div>
                        ) : r.actor_user_id ? (
                          <span className="font-mono">{r.actor_user_id.slice(0, 8)}… <span className="text-slate-400 font-sans">({r.actor_scope})</span></span>
                        ) : (
                          <span className="text-slate-400">— system</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-xs">{r.entity_type}</td>
                      <td className="px-4 py-2">
                        <span className={`inline-flex items-center rounded border px-2 py-0.5 text-xs ${ACTION_COLORS[r.action] || "border-slate-200 text-slate-700 bg-slate-50"}`}>{r.action}</span>
                      </td>
                      <td className="px-4 py-2 text-xs text-slate-500">{r.entity_id ? r.entity_id.slice(0, 8) + "…" : "—"}</td>
                    </tr>
                    {expanded === r.id && (
                      <tr className="bg-slate-50">
                        <td colSpan={5} className="px-4 py-3">
                          <pre className="text-xs overflow-auto whitespace-pre-wrap break-words">{JSON.stringify({ actor_user_id: r.actor_user_id, actor_scope: r.actor_scope, actor_name: r.actor_name, actor_email: r.actor_email, actor_role: r.actor_role, diff: r.diff, reason: r.reason, ip: r.ip, request_id: r.request_id }, null, 2)}</pre>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
